import React, { forwardRef } from 'react';
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';
import { InputAdornment, MenuItem, Typography } from '@mui/material';

import { HelperText } from '../HelperText';
import { Label } from '../Label';
import { TextFieldContainer } from '../TextFieldContainer';
import { mergeRefs } from '../utils';

import {
  StyledCountrySelect,
  StyledFormControl,
  StyledPhoneTextField,
} from './styles';
import type { IPhoneFieldProps } from './types';

import 'react-international-phone/style.css';

export const PhoneField = forwardRef<HTMLInputElement, IPhoneFieldProps>(
  (props, ref) => {
    const {
      onChange,
      value,
      fullWidth = true,
      label,
      error,
      size = 'medium',
      helperText,
      className,
      sx,
      defaultCountry = 'ru',
      ...otherProps
    } = props;

    const {
      inputValue,
      handlePhoneValueChange,
      inputRef,
      country,
      setCountry,
    } = usePhoneInput({
      defaultCountry,
      value,
      countries: defaultCountries,
      onChange: (data) => {
        if (onChange) {
          onChange(data.phone);
        }
      },
    });

    const mergedInputRef = mergeRefs(inputRef, ref);

    return (
      <TextFieldContainer fullWidth={fullWidth} className={className} sx={sx}>
        {label ? <Label error={error}>{label}</Label> : null}
        <StyledFormControl fullWidth={fullWidth} error={error}>
          <StyledPhoneTextField
            {...otherProps}
            value={inputValue}
            onChange={handlePhoneValueChange}
            type="tel"
            inputRef={mergedInputRef}
            size={size}
            error={error}
            placeholder={otherProps.placeholder ?? ''}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment
                    position="start"
                    style={{ marginRight: '2px', marginLeft: '-8px' }}
                  >
                    <StyledCountrySelect
                      value={country.iso2}
                      onChange={(e) =>
                        setCountry(e.target.value as CountryIso2)
                      }
                      renderValue={() => (
                        <FlagImage
                          iso2={country.iso2}
                          style={{ display: 'flex' }}
                        />
                      )}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            width: '210px',
                            maxHeight: '340px',
                          },
                        },
                      }}
                    >
                      {defaultCountries.map((c) => {
                        const countryData = parseCountry(c);
                        return (
                          <MenuItem
                            key={countryData.iso2}
                            value={countryData.iso2}
                          >
                            <FlagImage
                              iso2={countryData.iso2}
                              style={{ marginRight: '8px', flexShrink: 0 }}
                            />
                            <Typography
                              marginRight="8px"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flexGrow: 1,
                              }}
                            >
                              {countryData.name}
                            </Typography>
                            <Typography color="gray" sx={{ flexShrink: 0 }}>
                              +{countryData.dialCode}
                            </Typography>
                          </MenuItem>
                        );
                      })}
                    </StyledCountrySelect>
                  </InputAdornment>
                ),
              },
            }}
          />
          {helperText && <HelperText>{helperText}</HelperText>}
        </StyledFormControl>
      </TextFieldContainer>
    );
  }
);

PhoneField.displayName = 'PhoneField';
